export type ChatErrorType = 'network' | 'auth' | 'timeout' | 'server' | 'validation' | 'unknown';

export interface ClassifiedError {
    type: ChatErrorType;
    title: string;
    message: string;
    isRetryable: boolean;
}

/** Cancelamento esperado (troca de conversa, unmount, AbortSignal.timeout). */
export function isAbortError(error: unknown): boolean {
    if (!error) return false;
    if (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError') {
        return true;
    }
    if (error instanceof Error && error.name === 'AbortError') return true;
    if (typeof error === 'object') {
        const e = error as { name?: unknown; message?: unknown; code?: unknown; hint?: unknown; details?: unknown };
        if (String(e.name ?? '') === 'AbortError') return true;
        if (String(e.code ?? '').toLowerCase() === 'aborterror') return true;
        const blob = `${e.message ?? ''} ${e.hint ?? ''} ${e.details ?? ''}`.toLowerCase();
        if (blob.includes('aborterror') || blob.includes('signal is aborted') || blob.includes('request was aborted')) {
            return true;
        }
    }
    return false;
}

const EDGE_FUNCTION_HTTP_PATTERNS = [
    'Edge Function returned a non-2xx',
    'FunctionsHttpError',
];

/** Falha de rede / relay até a Edge Function (não inclui HTTP 4xx/5xx da function). */
const EDGE_FUNCTION_FETCH_PATTERNS = [
    'Failed to send a request to the Edge Function',
    'FunctionsFetchError',
    'FunctionsRelayError',
    'Relay Error invoking the Edge Function',
];

/** Palavras-chave para identificar erros de rede reais */
const NETWORK_ERROR_PATTERNS = [
    'Failed to fetch',
    'NetworkError',
    'net::ERR_',
    'Network request failed',
    'Load failed',
    'AbortError',
    'ERR_INTERNET_DISCONNECTED',
    'ERR_NAME_NOT_RESOLVED',
    'ECONNREFUSED',
    'ENOTFOUND',
    'fetch failed',
];

const AUTH_ERROR_PATTERNS = [
    'Token inválido',
    'Token ausente',
    'não autorizado',
    'Unauthorized',
    '401',
    'Usuário não autenticado',
    'Faça login novamente',
    'Session not found',
    'session_not_found',
];

const TIMEOUT_ERROR_PATTERNS = [
    'timeout',
    'ETIMEDOUT',
    'timed out',
];

const VALIDATION_ERROR_PATTERNS = [
    'Número inválido',
    'não possui WhatsApp',
    'não está registrado',
    'Nenhuma conversa selecionada',
    'Nenhuma conexão WhatsApp',
    'Digite uma mensagem antes de enviar',
    'Mensagem vazia',
    'janela de 24h do Instagram',
    'janela de 24h do WhatsApp',
];

function readStatus(source: unknown): number | null {
    if (!source || typeof source !== 'object' || !('status' in source)) return null;
    const status = Number((source as { status: unknown }).status);
    return Number.isFinite(status) ? status : null;
}

/** Status HTTP em FunctionsHttpError (context = Response) ou objetos com status. */
function extractHttpStatus(error: unknown): number | null {
    if (!error || typeof error !== 'object') return null;
    return readStatus((error as { context?: unknown }).context) ?? readStatus(error);
}

function classified(
    type: ChatErrorType,
    title: string,
    message: string,
    isRetryable: boolean,
): ClassifiedError {
    return { type, title, message, isRetryable };
}

/**
 * Classifica um erro e retorna informações amigáveis para o usuário.
 * Usa pattern matching contra a mensagem do erro para determinar o tipo.
 */
export function classifyError(error: unknown): ClassifiedError {
    const errorMessage = extractErrorMessage(error);
    const status = extractHttpStatus(error);

    // ChatSendPendingError (name) ou mensagem de outbox uncertain — não retentar.
    if (
        (error instanceof Error && error.name === 'ChatSendPendingError')
        || /confirmação do envio pendente|envio continua na fila|confira (a conversa|o whatsapp|o instagram) antes de (reenviar|tentar novamente)|não foi possível confirmar o envio|envio foi enfileirado/i
            .test(errorMessage)
    ) {
        return classified('validation', 'Envio pendente', errorMessage || 'Confirmação do envio pendente.', false);
    }

    // Erros de integração (Google/Meta token) — não confundir com sessão do app
    if (/token google expirado|reconecte a conta|integração google/i.test(errorMessage)) {
        return classified('validation', 'Integração expirada', errorMessage, false);
    }

    // 401/403 primeiro — FunctionsHttpError genérico NÃO é "sobrecarga"
    if (
        status === 401 ||
        status === 403 ||
        matchesPatterns(errorMessage, AUTH_ERROR_PATTERNS)
    ) {
        return classified('auth', 'Sessão expirada', 'Sua sessão expirou. Faça login novamente.', false);
    }

    if (
        status === 502 ||
        status === 503 ||
        status === 504 ||
        (matchesPatterns(errorMessage, EDGE_FUNCTION_FETCH_PATTERNS) && navigator.onLine)
    ) {
        return classified(
            'server',
            'Servidor temporariamente indisponível',
            'O servidor está temporariamente sobrecarregado. Aguarde alguns segundos e tente novamente.',
            true,
        );
    }

    if (matchesPatterns(errorMessage, NETWORK_ERROR_PATTERNS) || !navigator.onLine) {
        return classified(
            'network',
            'Problema de conexão',
            'Não foi possível conectar ao servidor. Verifique sua internet e tente novamente.',
            true,
        );
    }

    if (matchesPatterns(errorMessage, TIMEOUT_ERROR_PATTERNS)) {
        return classified(
            'timeout',
            'Servidor demorou para responder',
            'A requisição demorou muito. Tente novamente em alguns segundos.',
            true,
        );
    }

    if (matchesPatterns(errorMessage, EDGE_FUNCTION_HTTP_PATTERNS)) {
        return classified(
            'server',
            'Erro no servidor',
            'Não foi possível completar a operação. Tente novamente em instantes.',
            true,
        );
    }

    if (errorMessage.includes('janela de 24h')) {
        return classified('validation', 'Não é possível enviar', errorMessage, false);
    }

    if (errorMessage.includes('WhatsApp desconectado')) {
        return classified('validation', 'WhatsApp desconectado', errorMessage, false);
    }

    if (matchesPatterns(errorMessage, VALIDATION_ERROR_PATTERNS)) {
        return classified('validation', 'Dados inválidos', errorMessage, false);
    }

    // Erros de endpoint inexistente ou método não suportado (não faz sentido retentar)
    if (errorMessage.includes('405') || errorMessage.includes('Method Not Allowed') ||
        errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        return classified('validation', 'Endpoint indisponível', 'Este recurso não está disponível na API.', false);
    }

    // Erros genéricos de servidor
    if (errorMessage.includes('500') || errorMessage.includes('Internal') || errorMessage.includes('Proxy Error')) {
        return classified(
            'server',
            'Erro no servidor',
            'O servidor encontrou um problema. Tente novamente em alguns segundos.',
            true,
        );
    }

    return classified(
        'unknown',
        'Erro inesperado',
        errorMessage || 'Ocorreu um erro inesperado. Tente novamente.',
        true,
    );
}

/** Extrai mensagem de string ou Error, tratando objetos encadeados */
function extractErrorMessage(error: unknown): string {
    if (typeof error === 'string') return error;
    if (error instanceof Error) return error.message;
    if (error && typeof error === 'object' && 'message' in error) {
        return String((error as { message: unknown }).message);
    }
    return 'Erro desconhecido';
}

/** Verifica se a mensagem contém algum dos patterns fornecidos */
function matchesPatterns(message: string, patterns: string[]): boolean {
    const lowerMessage = message.toLowerCase();
    return patterns.some(p => lowerMessage.includes(p.toLowerCase()));
}
